import React, { useMemo, useCallback } from "react";
import { Layer, Source, useMap } from "react-map-gl/maplibre";
import { useStore } from "@/store/store";
import { useFlightRestrictionZonesQueryV1 } from "@/api/v1/flight-restriction-zone-api";
import type { FlightRestrictionZoneResponse } from "@/api/v1/flight-restriction-zone-api";
import { matchesZoneTypeFilter, matchesAltitudeRange, apiTypeToLabel } from "@/lib/zone-type-utils";

const NatsClickHandler = ({ zones }: { zones: FlightRestrictionZoneResponse[] }) => {
  const { current } = useMap();
  const { setSelectedZone, setZoneDrawerOpen } = useStore();

  const handleZoneClick = useCallback((event: unknown) => {
    if (!current || !zones) return;

    const e = event as { point: { x: number; y: number }; features?: unknown[] };
    
    // Get features from the click event or query rendered features
    const eventFeatures = Array.isArray(e.features) ? e.features : [];
    const features = eventFeatures.length > 0
      ? eventFeatures
      : current.queryRenderedFeatures([e.point.x, e.point.y], {
          layers: ["nats-fill"]
        });

    if (features && (features as unknown[]).length > 0) {
      const feature = features[0] as { id?: string | number; properties?: Record<string, unknown> };
      
      // Get zone ID from properties (most reliable) or feature id
      // Convert to string since zone.id is a string UUID
      const propsId = feature.properties?.id;
      const zoneId = propsId ? String(propsId) : (feature.id ? String(feature.id) : null);
      
      // Also try to match by designator as a fallback (more stable identifier)
      const designator = feature.properties?.designator as string | undefined;
      
      if (zoneId || designator) {
        // Find the full zone data - try ID first, then designator
        const zone = zones.find(z => 
          (zoneId && z.id === zoneId) || 
          (designator && z.designator === designator)
        );
        if (zone) {
          // Store the full zone object directly (type-safe)
          setSelectedZone(zone);
          setZoneDrawerOpen(true);
        }
      }
    }
  }, [current, zones, setSelectedZone, setZoneDrawerOpen]);

  // Cursor change handlers for hover feedback
  const handleMouseEnter = useCallback(() => {
    if (current) {
      current.getCanvas().style.cursor = 'pointer';
    }
  }, [current]);

  const handleMouseLeave = useCallback(() => {
    if (current) {
      current.getCanvas().style.cursor = '';
    }
  }, [current]);

  React.useEffect(() => {
    if (!current) return;

    // Wait for the layer to be added before registering events
    const setupEvents = () => {
      // Check if layer exists
      if (!current.getLayer("nats-fill")) {
        return;
      }

      current.on("click", "nats-fill", handleZoneClick);
      current.on("click", "nats-outline", handleZoneClick);
      current.on("mouseenter", "nats-fill", handleMouseEnter);
      current.on("mouseleave", "nats-fill", handleMouseLeave);
    };

    // If map is already loaded, try to setup immediately
    if (current.isStyleLoaded()) {
      setupEvents();
    }

    // Also listen for when layers are added
    current.on("styledata", setupEvents);

    return () => {
      current.off("click", "nats-fill", handleZoneClick);
      current.off("click", "nats-outline", handleZoneClick);
      current.off("mouseenter", "nats-fill", handleMouseEnter);
      current.off("mouseleave", "nats-fill", handleMouseLeave);
      current.off("styledata", setupEvents);
    };
  }, [current, handleZoneClick, handleMouseEnter, handleMouseLeave]);

  return <></>;
};

export const NatsLayer: React.FC = () => {
  const shouldShowFlightRestrictions = useStore((state) => state.shouldShowFlightRestrictions);
  const zoneTypeFilter = useStore((state) => state.zoneTypeFilter);
  const minAltitude = useStore((state) => state.minAltitude);
  const maxAltitude = useStore((state) => state.maxAltitude);
  
  // Fetch zones from v1 API
  const { data: zones, isLoading } = useFlightRestrictionZonesQueryV1();

  // Convert zones to GeoJSON FeatureCollection
  const geoJsonData = useMemo(() => {
    if (!zones || zones.length === 0) {
      return {
        type: 'FeatureCollection' as const,
        features: []
      };
    }

    const features = zones
      .filter(zone => {
        // Apply type filter using mapping utility
        return matchesZoneTypeFilter(zone.type, zoneTypeFilter);
      })
      .filter(zone => {
        // Apply altitude range filter (check if zone overlaps with operating altitude)
        return matchesAltitudeRange(
          zone.upper_limit,
          zone.lower_limit,
          minAltitude,
          maxAltitude
        );
      })
      .filter(zone => {
        // Ensure polygon exists and is valid coordinates array
        return zone.polygon && 
               Array.isArray(zone.polygon) &&
               zone.polygon.length > 0;
      })
      .map(zone => {
        // Convert raw coordinates array to GeoJSON MultiPolygon
        const geometry: GeoJSON.MultiPolygon = {
          type: 'MultiPolygon',
          coordinates: zone.polygon
        };
        
        return {
          type: 'Feature' as const,
          id: zone.id,
          geometry,
        properties: {
          id: zone.id,
          identifier: zone.identifier,
          name: zone.name,
          type: zone.type, // API type code (D, P, R)
          type_label: apiTypeToLabel(zone.type), // UI label (Danger, Prohibited, Restricted)
          local_type: zone.local_type,
          designator: zone.designator,
          designator_icao: zone.designator_icao,
          valid_time: zone.valid_time,
          effective_begin_time: zone.effective_begin_time,
          effective_end_time: zone.effective_end_time,
          upper_limit: zone.upper_limit,
          upper_limit_uom: zone.upper_limit_uom,
          upper_limit_reference: zone.upper_limit_reference,
          lower_limit: zone.lower_limit,
          lower_limit_uom: zone.lower_limit_uom,
          lower_limit_reference: zone.lower_limit_reference,
        }
        };
      });



    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [zones, zoneTypeFilter, minAltitude, maxAltitude]);


  // Hide layer if toggled off or loading
  if (!shouldShowFlightRestrictions || isLoading) {
    return null;
  }

  // Don't hide if zones is empty - let it render empty GeoJSON
  if (!zones || zones.length === 0) {
    return null;
  }

  return (
    <>
      <NatsClickHandler zones={zones} />
      <Source
        id="nats-source"
        type="geojson"
        data={geoJsonData}
      >
        {/* Fill layer - dynamic colors based on type */}
        <Layer
          id="nats-fill"
          type="fill"
          paint={{
            "fill-color": [
              "match",
              ["get", "type_label"],
              "Prohibited", "#ef4444", // red-500
              "Restricted", "#ef4444", // red-500
              "Danger", "#f97316",     // orange-500
              "#94a3b8" // slate-400 default
            ],
            "fill-opacity": 0.3,
          }}
        />

        {/* Outline layer - dynamic colors based on type */}
        <Layer
          id="nats-outline"
          type="line"
          paint={{
            "line-color": [
              "match",
              ["get", "type_label"],
              "Prohibited", "#ef4444", // red-500
              "Restricted", "#ef4444", // red-500
              "Danger", "#f97316",     // orange-500
              "#94a3b8" // slate-400 default
            ],
            "line-width": [
              "interpolate",
              ["linear"],
              ["zoom"],
              3,
              0.5,
              10,
              1.5,
              14,
              2.5,
            ],
            "line-opacity": 0.9,
          }}
        />

        {/* Labels visible at zoom 10+ */}
        <Layer
          id="nats-labels"
          type="symbol"
          minzoom={10}
          layout={{
            "text-field": ["get", "name"],
            "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
            "text-size": 11,
            "text-anchor": "center",
            "text-max-width": 10,
          }}
          paint={{
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 1.5,
          }}
        />
      </Source>
    </>
  );
};
